/* eslint-disable @typescript-eslint/no-unnecessary-condition */
/* eslint-disable consistent-return */

'use client'

import React, { useState, useMemo, useRef, useEffect } from 'react'
import {
  Box,
  Button,
  ButtonGroup,
  Chip,
  Divider,
  MenuItem,
  Skeleton,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { useTranslations } from 'next-intl'
import { appPaths } from '@config'
import {
  type BillingCycle,
  type Currency,
  type ProcessedPlanFeatures,
  type PricingApiResponse,
  usePricingData,
  type PlanInfo,
  PLAN_NAME_ENUM,
  transformFeatureDataForInfoTable,
} from '@query/page-query/pricing-query/pricing.model'
import { Link } from '@assets-components'
import { Icon, Image } from '@media-components'
import {
  ActionButton,
  SectionHeader,
  SectionWrapper,
  type SectionHeaderData,
} from '@components/common'
import { portalUrl } from '@utils/environment'
import { useHideElementOnScroll } from '@utils/hooks'
import { SCROLL_ID } from '@utils/scroll'
import { publicImages } from '@utils/shared'
import { commonStyles, getStyles, STYLE_SIZE_CONSTANTS } from '@utils/styles'
import {
  useCurrencies,
  usePricingFeatures,
  usePricingPlans,
} from 'src/client-query/pricing'
import { usePricingContext } from 'src/context'
import defaultStyles from './styles'

// Default currencies to use when API hasn't loaded yet
const DEFAULT_CURRENCIES = [
  { id: 'usd-default', code: 'USD', name: 'US Dollar', symbol: '$' },
  { id: 'inr-default', code: 'INR', name: 'Indian Rupee', symbol: '₹' },
]

type PlanCardProps = {
  title: string
  price: string | number
  currencySymbol: string
  period?: string
  billingDetails?: string
  buttonText: string
  buttonVariant?: 'outlined' | 'contained'
  features: ProcessedPlanFeatures
  isRecommended?: boolean
  highlight?: boolean
  credits: number
  isCustom?: boolean
  cardRef?: (el: HTMLDivElement | null) => void
}

function PlanCard({
  title,
  price,
  currencySymbol,
  period = 'per month',
  billingDetails = 'Billed Monthly',
  buttonText,
  features,
  isRecommended,
  highlight,
  credits,
  isCustom = false,
  cardRef,
}: PlanCardProps) {
  const styles = getStyles(defaultStyles)

  return (
    <Box
      {...styles('card', {
        ...(highlight && styles('highlightedCard').sx),
      })}
      ref={cardRef}
    >
      <Stack gap={2} height="100%">
        {/* Header */}
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography
            variant="h6.600"
            sx={{ fontSize: { xs: '24px', sm: '20px' } }}
          >
            {title}
          </Typography>
          {isRecommended && (
            <Chip
              variant="trans"
              label="✦ Recommended"
              color="primary"
              {...styles('recommendedChip')}
            />
          )}
        </Stack>

        {!isCustom ? (
          <>
            {typeof price !== 'string' && (
              <Typography variant="body2.500">
                <Typography variant="body1.600">{credits} credits</Typography>{' '}
                {period}
              </Typography>
            )}

            <Box>
              <Stack direction="row" gap={0.5}>
                <Typography variant="h5.600">
                  {typeof price === 'number'
                    ? `${currencySymbol}${price}`
                    : price}
                </Typography>

                <Stack>
                  {typeof price === 'number' && (
                    <>
                      <Typography variant="body3.500" color="text.secondary">
                        {currencySymbol === '$' ? 'USD' : 'INR'} {period}
                      </Typography>

                      <Typography variant="caption1.400" color="text.secondary">
                        {billingDetails}
                      </Typography>
                    </>
                  )}
                </Stack>
              </Stack>
            </Box>

            <Button
              variant={highlight ? 'contained' : 'outlined'}
              fullWidth
              size="large"
              component={Link}
              href={
                title.includes('Free')
                  ? `${portalUrl}register`
                  : appPaths.contactUs
              }
            >
              {buttonText}
            </Button>
          </>
        ) : (
          <Stack gap={1}>
            <Typography variant="h6.600">Custom</Typography>
            <Button
              variant={highlight ? 'contained' : 'outlined'}
              fullWidth
              size="large"
              component={Link}
              href={appPaths.contactUs}
            >
              Contact sales
            </Button>
          </Stack>
        )}

        {/* Quick Features */}
        <Stack gap={1} mt={1}>
          {features.quick.map((item, idx) => (
            <Stack key={idx} direction="row" gap={1} alignItems="flex-start">
              {item.icon && (
                <Icon iconName={item.icon} colorValues="primary.main" />
              )}
              <Typography variant="caption1.600">{item.text}</Typography>
            </Stack>
          ))}
        </Stack>

        <Stack gap={2} sx={commonStyles.hideElementOnMobile(true)}>
          <Divider />

          {/* Credits */}
          <Stack gap={1}>
            {features.credits.map((item, idx) => (
              <Box key={idx} {...styles('creditsRow')}>
                <Typography variant="caption1.500">{item.text}</Typography>
                <Stack direction="row" alignItems="center" gap={0.5}>
                  <Image
                    src={publicImages.coin}
                    alt="coin"
                    sx={{ width: '1rem' }}
                    style={{ width: '1rem', height: '1rem' }}
                  />
                  <Typography
                    variant="caption1.700"
                    sx={{ marginBottom: '4px' }}
                  >
                    {item.value}
                  </Typography>
                </Stack>
              </Box>
            ))}
          </Stack>

          <ActionButton
            variant="text"
            showArrow
            sx={{
              textDecoration: 'underline',
              paddingLeft: '0px !important',
              '&:hover': {
                textDecoration: 'underline !important',
                background: 'transparent !important',
              },
            }}
          >
            Show More
          </ActionButton>

          {/* Divider */}
          <Divider />

          {/* Checklist */}
          <Stack gap={2} flexGrow={1}>
            <Typography variant="body2.600">
              {features.checklistTitle}
            </Typography>
            <Stack gap={1}>
              {features.checklist.map((item, idx) => (
                <Stack key={idx} direction="row" gap={1.5} alignItems="start">
                  <Icon iconName="Tick" colorValues="green.600" />
                  <Typography variant="caption1.500">{item}</Typography>
                </Stack>
              ))}
            </Stack>
          </Stack>
        </Stack>
      </Stack>
    </Box>
  )
}

function TableValue({ value }: { value: PlanInfo }) {
  if (value.available && value.value === '0') {
    return (
      <Box sx={{ color: 'success.main' }}>
        <Icon
          iconName="TickCloseCircleSolid"
          type="solid"
          currentValue={false}
        />
      </Box>
    )
  }

  return value.value ? (
    <Typography
      variant="caption1.500"
      textAlign="center"
      sx={{
        fontSize: '0.75rem',
      }}
    >
      {value.value === '-1' ? 'Unlimited' : value.value}
    </Typography>
  ) : (
    <Typography variant="caption1.500">-</Typography>
  )
}

export type OurPlansData = {
  sectionHeaderData: SectionHeaderData
  pricingData: PricingApiResponse
}

export function OurPlans({ sectionHeaderData }: OurPlansData) {
  const [billingCycle, setBillingCycle] = useState<BillingCycle>('ANNUAL')
  const [selectedPlanTab, setSelectedPlanTab] = useState<string>('Premium')
  const styles = getStyles(defaultStyles)

  const { currentPlanType, getCurrentPricing, currency, setCurrency } =
    usePricingContext()

  const plansParams = useMemo(() => {
    return {
      currency,
      include_features: true,
    }
  }, [currency])

  const { plans: pricingDataFromApi, isLoading: isPlansLoading } =
    usePricingPlans({
      autoFetch: true,
      params: plansParams,
    })

  // Fetch available currencies
  const { currencies: currenciesFromApi } = useCurrencies({
    autoFetch: true,
  })

  // Fetch features for mobile comparison table
  const { features: featuresData } = usePricingFeatures({
    autoFetch: true,
  })

  const transformedFeatures = useMemo(() => {
    return featuresData ? transformFeatureDataForInfoTable(featuresData) : []
  }, [featuresData])

  // Use API currencies or fallback to defaults
  const currencies = currenciesFromApi ?? DEFAULT_CURRENCIES

  const dynamicPricing = getCurrentPricing(billingCycle)

  const { pricingCards } = usePricingData({
    apiData: pricingDataFromApi,
    currency,
    billingCycle,
    currentPlanType,
    dynamicPricing,
  })

  const isLoading = useMemo(
    () => isPlansLoading || (!isPlansLoading && !pricingDataFromApi),
    [isPlansLoading, pricingDataFromApi]
  )

  const t = useTranslations('Pricing.ourPlans')

  const isCurrentPlanEnterpise = useMemo(
    () => currentPlanType === 'ENTERPRISE',
    [currentPlanType]
  )

  const [isFooterVisible, setIsFooterVisible] = useState(false)

  // Create refs for each card
  const cardRefs = useRef<Record<string, HTMLDivElement | null>>({})

  // Function to set ref for a specific card
  function setCardRef(title: string) {
    return (el: HTMLDivElement | null) => {
      cardRefs.current[title] = el
    }
  }

  useEffect(() => {
    const cardElement = cardRefs.current[selectedPlanTab]
    if (!cardElement) return

    const observer = new IntersectionObserver(
      ([entry]) => {
        // Show footer button only when card is out of view AND above viewport
        const isOutOfView = !entry.isIntersecting
        const isAbove = entry.boundingClientRect.top < 0
        setIsFooterVisible(isOutOfView && isAbove)
      },
      {
        threshold: 0.7,
        rootMargin: '0px',
      }
    )

    observer.observe(cardElement)
    return () => observer.disconnect()
  }, [selectedPlanTab, isLoading])

  // Hook to detect scroll direction for bottom nav sync
  const { isVisible: isNavVisible } = useHideElementOnScroll({ threshold: 10 })

  return (
    <>
      <Stack
        spacing={1}
        {...styles('fixedChoosePlanButton', {
          ...(isFooterVisible && styles('fixedChoosePlanButtonVisible').sx),
          bottom: isNavVisible
            ? `${STYLE_SIZE_CONSTANTS.mobileBottomNavbarHeight}px`
            : '0px',
        })}
      >
        <Button
          variant="contained"
          sx={{
            minWidth: '100%',
          }}
          component={Link}
          href={
            selectedPlanTab.includes('Free')
              ? `${portalUrl}register`
              : appPaths.contactUs
          }
        >
          {pricingCards?.find((card) => card.title === selectedPlanTab)
            ?.buttonText ?? 'Choose Plan'}
        </Button>
        <Typography variant="caption2.600">
          💸 Free Demo Available. No Credit Card Required
        </Typography>
      </Stack>
      <SectionWrapper
        useAsStickyWrapper
        csx={{
          wrapper: {
            paddingTop: {
              xs: '0px',
              sm: '48px',
            },
            paddingBottom: {
              xs: '0px',
              sm: '48px',
            },
          },
        }}
      >
        <Stack spacing={4}>
          <SectionHeader {...sectionHeaderData} />

          <Box {...styles('wrapper')}>
            <Stack gap={2.5} {...styles('stickyControl')}>
              <Box {...styles('controlWrapper')}>
                <Stack
                  alignItems="center"
                  direction="row"
                  {...styles('billingToggleWrapper')}
                >
                  <Button
                    size="small"
                    value="MONTH"
                    variant={billingCycle === 'MONTH' ? 'outlined' : 'text'}
                    selected={billingCycle === 'MONTH'}
                    onClick={() => {
                      setBillingCycle('MONTH')
                    }}
                    {...styles('billingButton')}
                  >
                    <Typography variant="caption1.700">Monthly</Typography>
                  </Button>
                  <Button
                    size="small"
                    value="ANNUAL"
                    onClick={() => {
                      setBillingCycle('ANNUAL')
                    }}
                    selected={billingCycle === 'ANNUAL'}
                    variant={billingCycle === 'ANNUAL' ? 'outlined' : 'text'}
                    {...styles('billingButton', {
                      padding: '6px 18px !important',
                    })}
                  >
                    <Box {...styles('annualBillingLayout')}>
                      <Typography variant="caption1.700">Annual</Typography>
                      {dynamicPricing?.discount ? (
                        <Chip
                          variant="trans"
                          color="success"
                          label={
                            <Typography variant="caption2.700">
                              {t('buttonSavingText', {
                                discount: dynamicPricing?.discount,
                              })}
                            </Typography>
                          }
                          size="small"
                          {...styles('annualChip')}
                        />
                      ) : null}
                    </Box>
                  </Button>
                </Stack>

                <Box>
                  <TextField
                    select
                    value={currency}
                    onChange={(e) => setCurrency(e.target.value as Currency)}
                    variant="outlined"
                    size="small"
                    {...styles('currencySelect')}
                  >
                    {currencies.map((currencyOption) => (
                      <MenuItem
                        key={currencyOption.id}
                        value={currencyOption.code}
                      >
                        <Typography variant="caption1.600">
                          {currencyOption.symbol} {currencyOption.code}
                        </Typography>
                      </MenuItem>
                    ))}
                  </TextField>
                </Box>
              </Box>
              <ButtonGroup {...styles('mobileTabsWrapper')}>
                {pricingCards?.map((card) => (
                  <Button
                    key={card.id}
                    onClick={() => {
                      setSelectedPlanTab(card.title)
                      setCardRef(card.title)
                    }}
                    selected={selectedPlanTab === card.title}
                    sx={{
                      flex: 1,
                    }}
                  >
                    <Typography variant="caption1.700">{card.title}</Typography>
                  </Button>
                ))}
              </ButtonGroup>
            </Stack>

            {/* Desktop Cards */}
            <Box
              {...styles('cardsContainer', {
                ...styles('desktopGridWrapper').sx,
                display: { xs: 'none', sm: 'flex' },
              })}
            >
              {isLoading
                ? // Loading skeletons
                  Array.from({ length: 4 }).map((_, idx) => (
                    <Skeleton
                      key={idx}
                      variant="rounded"
                      width="100%"
                      height={600}
                      sx={{ borderRadius: '1.5rem' }}
                    />
                  ))
                : pricingCards?.map((card) => (
                    <PlanCard
                      key={card.id}
                      {...card}
                      billingDetails={
                        billingCycle === 'MONTH'
                          ? 'Billed Monthly'
                          : 'Billed Annually'
                      }
                      isCustom={isCurrentPlanEnterpise}
                    />
                  ))}
            </Box>

            {/* Mobile View with Tabs and Feature Table */}
            <Box
              sx={{
                display: { xs: 'flex', sm: 'none' },
                flexDirection: 'column',
                width: '100%',
                gap: 2,
              }}
            >
              {/* Mobile Plan Tabs */}

              {/* Selected Plan Card */}
              <Box>
                {isLoading ? (
                  <Skeleton
                    variant="rounded"
                    width="100%"
                    sx={{
                      borderRadius: '1.5rem',
                      height: 350,
                    }}
                  />
                ) : (
                  pricingCards
                    ?.filter((card) => card.title === selectedPlanTab)
                    .map((card) => (
                      <PlanCard
                        key={card.id}
                        {...card}
                        billingDetails={
                          billingCycle === 'MONTH'
                            ? 'Billed Monthly'
                            : 'Billed Annually'
                        }
                        isCustom={isCurrentPlanEnterpise}
                        cardRef={setCardRef(card.title)}
                      />
                    ))
                )}
              </Box>

              {/* Mobile Feature Comparison Table */}
              <Box {...styles('mobileFeatureTable')}>
                <Box {...styles('mobileCategoryHeader')}>
                  <Typography variant="body3.600">Key Plan Features</Typography>
                </Box>

                {transformedFeatures.map((category) => (
                  <React.Fragment key={category.category}>
                    <Box
                      sx={{
                        padding: '8px 16px',
                        borderBottom: '1px solid',
                        borderColor: 'border.dark',
                      }}
                    >
                      <Typography variant="body3.600">
                        {category.category.split('_').join(' ')}
                      </Typography>
                    </Box>

                    {category.data.map((row) => (
                      <Box
                        key={row.feature_name}
                        {...styles('mobileFeatureRow')}
                      >
                        <Box {...styles('mobileFeatureName')}>
                          <Typography
                            variant="caption1.500"
                            color="text.secondary"
                          >
                            {row.feature_name}
                          </Typography>
                        </Box>
                        <Box {...styles('mobileValuesContainer')}>
                          {pricingCards?.map((card) => {
                            const planValue =
                              row.plan_info.find(
                                (p) =>
                                  p.plan_name === card.title ||
                                  (card.title === 'Free' &&
                                    (p.plan_name as string) === 'Trial')
                              ) ||
                              (card.title === 'Free'
                                ? row.plan_info.find(
                                    (p) => p.plan_name === PLAN_NAME_ENUM.GROWTH
                                  )
                                : null) ||
                              ({ value: '', available: false } as PlanInfo)

                            return (
                              <Box
                                key={card.id}
                                {...styles('mobileValueCell', {
                                  backgroundColor:
                                    selectedPlanTab === card.title
                                      ? '#a3a1ea1A'
                                      : 'transparent',
                                })}
                              >
                                <TableValue value={planValue} />
                              </Box>
                            )
                          })}
                        </Box>
                      </Box>
                    ))}
                  </React.Fragment>
                ))}
              </Box>
            </Box>

            {/* FIXME: IF we needed it */}
            {/* Mobile Carousel */}
            {/* <Box {...styles('mobileCarouselWrapper')}>
            <CarouselSection
              swiperOptions="autoSlideView"
              realSwiperOptions={{
                pagination: {
                  clickable: true,
                },
                autoplay: {
                  delay: 3000,
                  disableOnInteraction: false,
                },
                loop: true,
              }}
              disableArrow
            >
              {pricingCards.map((card) => (
                <SwiperSlide key={card.id}>
                  <Box padding="12px">
                    <PlanCard
                      {...card}
                      billingDetails={
                        billingCycle === 'MONTH'
                          ? 'Billed Monthly'
                          : 'Billed Annually'
                      }
                    />
                  </Box>
                </SwiperSlide>
              ))}
            </CarouselSection>
          </Box> */}
          </Box>
          <ActionButton
            variant="outlined"
            link={`#${SCROLL_ID.COMPARE_PLANS}`}
            showArrow
            {...styles('comparePlansButton')}
            iconSx={{
              transform: 'rotate(90deg)',
            }}
          >
            Compare Plans
          </ActionButton>
        </Stack>
      </SectionWrapper>
    </>
  )
}
